import axiosInstance from "@/lib/axiosInstance";
import { userManagementEndpoints } from "./userManagement.endpoints";
import { getUserById } from "./userManagement.queries";
import { usersDirectoryClient } from "./usersDirectory.client";
import type { UserRecord } from "./usersDirectory.types";
import type { CustomAxiosRequestConfig } from "@/types/common.types";

export const UserRecordQueryKeys = {
  record: "userRecord",
} as const;

/**
 * The person behind a record page. A person who cannot be opened sends the admin back to the
 * list with its own message, so the generic error toast is suppressed here.
 */
export const fetchUserRecord = async (
  userId: string,
  signal?: AbortSignal,
): Promise<UserRecord> => {
  const { data } = await getUserById(userId, signal, {
    suppressErrorAlert: true,
  } as CustomAxiosRequestConfig);
  return usersDirectoryClient.decorateUserDetail(data);
};

/**
 * The person read made ahead of a write answered 404: the person was removed, or left what this
 * login can see, after the page opened. The write's own 404 is about what it writes, so the two
 * are told apart by this.
 */
export class PersonNotFoundError extends Error {
  constructor() {
    super("The person could not be found");
    this.name = "PersonNotFoundError";
  }
}

export type UserProfileUpdate = {
  firstName: string;
  lastName: string;
  phoneNumber?: string;
  reportsTo?: string;
};

/**
 * Only the profile fields. The update endpoint treats a present `entityIds` as the person's whole
 * membership and role lists as changes, so leaving them out is what keeps this save to the profile.
 */
export const updateUserProfile = async (userId: string, payload: UserProfileUpdate) => {
  const { data } = await axiosInstance.put(`${userManagementEndpoints.users}/${userId}`, payload, {
    suppressErrorAlert: true,
  } as CustomAxiosRequestConfig);
  return data;
};

/**
 * Sets the person's entities. The update takes `entityIds` as their complete membership list and
 * removes every entity left out, so the caller sends all current ones plus the new one. Only a
 * Super Admin may change a person's entities (backend SERV-3400), and only a Super Admin is
 * shown every entity the person is in.
 */
export const updateUserEntities = async (userId: string, entityIds: string[]) => {
  await axiosInstance.put(`${userManagementEndpoints.users}/${userId}`, { entityIds }, {
    suppressErrorAlert: true,
  } as CustomAxiosRequestConfig);
};
